import { Box, Text, TextInput, Stack, Button } from '@mantine/core';
import { renderRichTextWithVars, VAR_BUBBLE_CSS } from '@/features/editor/utils/renderRichText';
import { NAVY, inputStyles } from './styles';

export function StartNode() {
  return (
    <Box>
      <Text size="xl" fw={700} mb="xs" c="#2A3140">
        Welcome
      </Text>
      <Text size="sm" c="#4E5A6B">
        Click "Next Step" to begin
      </Text>
    </Box>
  );
}

export function NoteNode({ content, sessionState }: { content: string; sessionState: Record<string, any> }) {
  return (
    <Box>
      <style>{VAR_BUBBLE_CSS}</style>
      <div
        className="rich-text-render"
        dangerouslySetInnerHTML={{ __html: renderRichTextWithVars(content || '', sessionState) }}
        style={{ fontSize: '0.875rem', color: '#4E5A6B', lineHeight: 1.6 }}
      />
    </Box>
  );
}

interface PromptNodeProps {
  content: string;
  varId: string;
  value: string;
  onChange: (value: string) => void;
}

export function PromptNode({ content, value, onChange }: PromptNodeProps) {
  return (
    <Box>
      <Text size="sm" c="#4E5A6B" mb="md">
        {content}
      </Text>
      <TextInput
        label="Your Answer"
        placeholder="Enter your response"
        value={value}
        onChange={(e) => onChange(e.currentTarget.value)}
        styles={inputStyles}
        radius="md"
      />
    </Box>
  );
}

interface FormNodeProps {
  title: string;
  description?: string;
  fields: any[];
  input: Record<string, any>;
  onInputChange: (input: Record<string, any>) => void;
  submitLabel?: string;
}

export function FormNode({ title, description, fields, input, onInputChange }: FormNodeProps) {
  return (
    <Box>
      <Text size="xl" fw={700} mb={4} c="#2A3140">
        {title}
      </Text>
      {description && (
        <Text size="sm" c="#4E5A6B" mb="lg">
          {description}
        </Text>
      )}
      <Stack gap="sm">
        {fields.map((field: any) => (
          <TextInput
            key={field.name}
            label={field.label}
            placeholder={`Enter ${field.label.toLowerCase()}`}
            required={field.required}
            type={field.type === 'email' ? 'email' : field.type === 'tel' ? 'tel' : 'text'}
            value={input[field.name] || ''}
            onChange={(e) => onInputChange({ ...input, [field.name]: e.currentTarget.value })}
            styles={inputStyles}
            radius="md"
          />
        ))}
      </Stack>
    </Box>
  );
}

interface BranchNodeProps {
  title?: string;
  branches: any[];
  onSelect: (target: string) => void;
  onAdvance: () => void;
}

export function BranchNodeRenderer({ title, branches, onSelect, onAdvance }: BranchNodeProps) {
  return (
    <Box>
      {title && (
        <Text size="xl" fw={700} mb="lg" c="#2A3140">
          {title}
        </Text>
      )}
      <Stack gap="sm">
        {branches.map((branch: any, index: number) => (
          <Button
            key={index}
            variant="outline"
            color={NAVY}
            radius="md"
            onClick={() => {
              onSelect(branch.target);
              setTimeout(onAdvance, 50);
            }}
            fullWidth
            justify="flex-start"
          >
            {branch.label || `Option ${index + 1}`}
          </Button>
        ))}
      </Stack>
    </Box>
  );
}

export function EndNode({ outcome }: { outcome?: string }) {
  const label = outcome && outcome !== 'success' ? outcome : 'This flow has reached its final step.';

  return (
    <Box ta="center" py="lg">
      <Text size="xl" fw={700} mb="xs" c="#415A91">
        Flow complete
      </Text>
      <Text size="sm" c="#4E5A6B">
        {label}
      </Text>
    </Box>
  );
}

export function UnknownNode({ type }: { type: string }) {
  return (
    <Box>
      <Text size="sm" c="dimmed">
        Node type: {type}
      </Text>
    </Box>
  );
}
